import { ClerkProvider } from '@clerk/clerk-react';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { startTheme, useTheme } from './theme';

const PUBLISHABLE_KEY = import.meta.env.VITE_PUBLISHABLE_KEY as string | undefined;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_PUBLISHABLE_KEY. Copy .env.example to .env.local.');
}

// Stamp the theme before the first paint, so nothing flashes the other one.
startTheme();

/**
 * Clerk's own menus follow Artwork's palette, so signing in and switching workspace
 * don't flash a white panel over the canvas — and they follow it as it changes, rather
 * than keeping whichever theme the page loaded with.
 *
 * In a file of its own, away from `createRoot`, and main.tsx must not reach the theme except
 * through here: a saved theme.ts re-ran any module that both drew a component and read the
 * theme, and in main.tsx that mounted a second app over the first. Two ClerkProviders on one
 * page threw, the editor was rebuilt, and the selection and the view went with it (1 Nov).
 */
export function Root() {
  const { palette } = useTheme();
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY!}
      afterSignOutUrl="/"
      appearance={{
        variables: {
          colorPrimary: palette.accent,
          colorBackground: palette.surface,
          colorText: palette.ink,
          colorTextSecondary: palette.inkMuted,
          colorInputBackground: palette.surfaceRaised,
          colorInputText: palette.ink,
          colorNeutral: palette.ink,
          borderRadius: '10px',
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif",
        },
      }}
    >
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ClerkProvider>
  );
}
